/**
 * BrandTokenProvider — the ONE scoped runtime brand writer (CON-5).
 *
 * Named as this package's deliverable in `src/contract.md`. React is the only
 * import; it is declared as a PEER dependency on this subpath
 * (`@charitystack/design-tokens/provider`) so the values entrypoint (`.`) and
 * the CSS files stay dependency-free, per the package's standing rule.
 *
 * The palette expander is a PROP, not an import. That is what keeps this package
 * off `packages/shared` (48 runtime deps behind a React peer dep — the failure
 * mode SPEC §2 records). `packages/shared/src/styling/BrandColorProvider.jsx`
 * is the thin binding that pre-supplies `generateBrandColors`.
 *
 * Scope (D6): the caller names the element. There is no default of
 * `document.documentElement`, and `applyBrandTokens` refuses it outright.
 * Two shapes are supported, matching the two good writers already in the repo:
 *
 *   1. `<BrandTokenProvider scope={ref} palette={p}>…</BrandTokenProvider>`
 *      — imperative write onto an element the caller owns. Use this when the
 *        subtree portals out of its React parent (Radix/vaul render into
 *        `document.body`), because the scope must be an ancestor of every
 *        reader, not of every React child.
 *   2. `const style = useBrandTokenStyle({ palette })` → `<main style={style}>`
 *      — no DOM write at all, the pattern
 *        `portal/.../UpdateRecurringDonation.jsx:1321-1328` and
 *        `hosted-page/src/UnsubscribePage.tsx:144-155` already use.
 */

import React from 'react';

import {
  applyBrandTokens,
  auditBrandedSurfaces,
  resolveBrandTokens,
} from './brandTokens.js';

function resolveScope(scope) {
  if (!scope) return null;
  if (typeof scope === 'function') return scope() || null;
  if (typeof scope === 'object' && 'current' in scope) return scope.current || null;
  return scope;
}

/**
 * Compute the inline-style object for a container the caller renders itself.
 *
 * @param {{palette?: object, color?: string, expand?: Function}} input
 * @returns {React.CSSProperties|undefined} `undefined` when there is nothing to
 *   write — so an unbranded surface keeps its stylesheet fallbacks (the
 *   unsubscribe page's `var(--brand-50, var(--sage))` chain depends on this).
 */
export function useBrandTokenStyle({ palette, color, expand } = {}) {
  return React.useMemo(() => {
    const tokens = resolveBrandTokens({ palette, color, expand });
    return Object.keys(tokens).length > 0 ? tokens : undefined;
  }, [palette, color, expand]);
}

/**
 * @param {object} props
 * @param {HTMLElement|React.RefObject<HTMLElement>|(() => HTMLElement)} props.scope
 *   The element the tokens are written to. Required.
 * @param {object} [props.palette] Already-expanded `accent-*` palette.
 * @param {string} [props.color] Base brand hex (needs `expand`).
 * @param {(hex: string) => object} [props.expand] Ramp generator.
 * @param {boolean} [props.audit] Run the dev-only unbranded-surface audit (D16).
 * @param {React.ReactNode} props.children
 */
export function BrandTokenProvider({
  scope,
  palette,
  color,
  expand,
  audit = false,
  children,
}) {
  const tokens = React.useMemo(
    () => resolveBrandTokens({ palette, color, expand }),
    [palette, color, expand],
  );

  // THE RESOLVED ELEMENT IS TRACKED IN STATE, and that is the whole point of
  // this block rather than an incidental style choice.
  //
  // `scope` is commonly a REF OBJECT, whose identity never changes, and a ref
  // is null until React attaches it. A consumer whose scope element arrives
  // LATE — it renders a placeholder first and the real container second, or the
  // container is conditional — therefore mounted this provider, resolved
  // `null`, bailed, and never ran again, because neither `scope` nor `tokens`
  // had changed by the time the element appeared. The brand write was silently
  // lost, with no error and no unbranded-looking failure on the surfaces that
  // happen to read a `var()` fallback. Found by adversarial review of CON-157,
  // reproduced in the test suite ("writes when the scope element appears after
  // mount"), and fixed HERE rather than at one call site: "resolve once at
  // mount, never retry" is wrong for every consumer, not just that one.
  //
  // The resolver has no dependency array on purpose — it re-resolves after each
  // commit — and only writes state when the answer actually changes, so it
  // settles rather than looping.
  const [element, setElement] = React.useState(null);
  React.useLayoutEffect(() => {
    const next = resolveScope(scope);
    setElement((previous) => (previous === next ? previous : next));
  });

  // Layout effect so the tokens land before the browser paints the subtree —
  // the imperative writers this replaces ran in useEffect and could show one
  // unbranded frame.
  React.useLayoutEffect(() => {
    if (!element) return undefined;
    if (Object.keys(tokens).length === 0) return undefined;
    return applyBrandTokens(element, tokens);
  }, [element, tokens]);

  React.useEffect(() => {
    if (!audit) return;
    auditBrandedSurfaces(element);
  }, [audit, element, tokens]);

  return children;
}

export default BrandTokenProvider;
